import { Button, Modal, Offcanvas } from 'react-bootstrap';
import { deleteUserRequestData, getSearchStoreUser } from '@dataTypes/grantAuthority';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { AxiosHeaders } from 'axios';
import { imgTypes } from '@dataTypes/imgTypes';
import errorOrYesModalState from '../../recoil/errorOrYesModal/atom';
import { GRANTAUTHORITY } from './queryKey/queryKey';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteManager, deleteUser } from '@apis/grantAuthority';
import { grantUserListData } from '@dataTypes/grantAuthority';
import { AUTH } from '@dataTypes/authTypes';
import { convertUserType } from '@services/common/roleToKorean';

// CSS ShowConfirmMemberModal.tsx 공통 사용
interface DeleteUserModalProps {
  closeDeleteUserModal: () => void;
  modifyUser: grantUserListData | undefined;
  deleteUserModal: boolean;
  authority: AUTH | undefined;
  storeId: number | undefined;
}

const DeleteUserModal = ({
  closeDeleteUserModal,
  modifyUser,
  deleteUserModal,
  authority,
  storeId,
}: DeleteUserModalProps) => {
  const setErrorState = useSetRecoilState(errorOrYesModalState);
  const queryClient = useQueryClient();
  const formattedPhoneNumber = (item: string) => {
    if (!item) return '';
    return item.slice(0, 7) + '****';
  };

  const { mutate: deleteUserMutate } = useMutation(
    (data: deleteUserRequestData) => deleteUser(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GRANTAUTHORITY.GRANTAUTHORITYLISTS]);
        closeDeleteUserModal();
      },
      onError: (error: AxiosHeaders) => {
        if (error.response.status === 400) {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: '요청이 거부되었습니다.',
            text: '이미 권한이 있는 지점이거나, <br /> 검색된 사용자 오류입니다.',
            modalBG: true,
          });
        } else if (error.response.status === 403) {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: '권한이 없어 요청이 거부되었습니다.',
            text: '관리자에게 권한을 요청하거나, <br /> 해당 기능의 권한 여부를 확인해주세요.',
            modalBG: true,
          });
        } else {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: '알 수 없는 오류입니다.',
            text: '로그아웃 후 재시도 해주세요. <br /> 해당 오류가 계속되면 관리자에게 문의해주세요.',
            modalBG: true,
          });
        }
      },
    },
  );

  const { mutateAsync: deleteManagerMutateAsync } = useMutation(
    (data: deleteUserRequestData) => deleteManager(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GRANTAUTHORITY.GRANTAUTHORITYLISTS]);
      },
      onError: (error: AxiosHeaders) => {
        if (error.response.status === 400) {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: '요청이 거부되었습니다.',
            text: '이미 권한이 있는 지점이거나, <br /> 검색된 사용자 오류입니다.',
            modalBG: true,
          });
        } else if (error.response.status === 403) {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: '권한이 없어 요청이 거부되었습니다.',
            text: '관리자에게 권한을 요청하거나, <br /> 해당 기능의 권한 여부를 확인해주세요.',
            modalBG: true,
          });
        } else {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: '알 수 없는 오류입니다.',
            text: '로그아웃 후 재시도 해주세요. <br /> 해당 오류가 계속되면 관리자에게 문의해주세요.',
            modalBG: true,
          });
        }
      },
    },
  );

  const deleteUserHandler = async () => {
    if (!modifyUser) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '삭제할 사용자를 선택해주세요',
        text: '사용자가 선택되지 않았습니다.',
        modalBG: true,
      });
      return;
    }
    if (!storeId) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '지점을 선택해주세요.',
        text: '지점이 선택되지 않았습니다.',
        modalBG: true,
      });
      return;
    }

    const data = {
      loginId: modifyUser?.login_id,
      storeId: storeId,
    };

    const masterDeletesElevatedStoreRole =
      authority === AUTH.MASTER &&
      (modifyUser.authority === AUTH.MANAGER || modifyUser.authority === AUTH.ADMIN);

    if (masterDeletesElevatedStoreRole) {
      const deleteManagerResult = await deleteManagerMutateAsync(data);
      if (deleteManagerResult) {
        closeDeleteUserModal();
      }
    } else if (
      authority === AUTH.ADMIN &&
      (modifyUser.authority === AUTH.MANAGER || modifyUser.authority === AUTH.ADMIN)
    ) {
      deleteUserMutate(data);
    } else if (modifyUser.authority === AUTH.MANAGER || modifyUser.authority === AUTH.ADMIN) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '권한이 없어 요청이 거부되었습니다.',
        text: '관리자에게 권한을 요청하거나, <br /> 해당 기능의 권한 여부를 확인해주세요.',
        modalBG: true,
      });
    } else {
      deleteUserMutate(data);
    }
  };

  return (
    <>
      <Modal
        id={'show_member_detail'}
        className="delete_user_modal"
        show={deleteUserModal}
        onHide={closeDeleteUserModal}
        centered
      >
        <Modal.Header closeButton className="logout_header_wrapper">
          <Modal.Title className="cart_header_text">권한 삭제</Modal.Title>
        </Modal.Header>
        <Modal.Body className="member_modal_body">
          <div className="member_text">
            <div className="text">
              <span>이름: </span>
              <span className="strong">{modifyUser?.name}</span>
            </div>
            <div className="text">
              <span>아이디: </span>
              <span className="strong">{modifyUser?.login_id}</span>
            </div>
            <div className="text">
              <span>휴대폰 번호: </span>
              <span className="strong">
                {formattedPhoneNumber(modifyUser?.phone_number as string)}
              </span>
            </div>
            <div className="text">
              <span>권한: </span>
              <span className="strong">{convertUserType(modifyUser?.authority as AUTH)}</span>
            </div>
          </div>

          <div className="delete_info_text">권한 삭제 후 복구하시려면 재등록 해주세요.</div>
        </Modal.Body>

        <Modal.Footer className="delete_member_footer_wrapper">
          <Button className="member_btn confirm" variant="secondary" onClick={deleteUserHandler}>
            삭제
          </Button>
          <Button className="member_btn cancel" variant="secondary" onClick={closeDeleteUserModal}>
            취소
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeleteUserModal;
